import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../common/api";
import { computeMotorRight } from "../components/rawsurface/shaftRanges";

/**
 * Converts kept circles into the tab-separated form scan/parse expects.
 *
 * Mapping matches production's own output exactly - verified against a real lineResults file,
 * where each line is (x, y = circle centre X, z = circle centre Y, r = radius). Only kept
 * circles are emitted: dropping a circle is precisely what the acceptance thresholds mean, so
 * including rejects would compute a shim from data production would never have used.
 *
 * All values are microns, matching TSVData's stated contract. Lives here (not in
 * RawSurfaceSaveDialog.js, which originally defined it) since applyRawSurfaceFilters needs it too
 * and a reducer file importing from a component would be the wrong direction.
 */
export const circlesToTsv = (circles) =>
    circles
        .filter((c) => c.kept && c.pointCount > 0)
        .map((c) => `${c.x}\t${c.centerY}\t${c.centerZ}\t${c.radius}`)
        .join("\n");

// Production's own acceptance thresholds (GenerateTSV defaults). Shown as the starting
// position so the view opens on "what production would have kept" before anything is explored.
export const PRODUCTION_ANGLE_LIMIT = 45;
export const PRODUCTION_POINT_LIMIT = 300;
// No production equivalent (diagnostic only) - just a reasonable starting slider position for
// when the switch is first turned on.
export const DEFAULT_MAX_SIGMA = 100;
// AU/AUSettings.cs's CircleFixing.MicronLimit, live as of 2026-08-13 - flagged there as
// provisional/under re-evaluation, so this is a single place to update if it changes.
export const PRODUCTION_CIRCLE_FIX_LIMIT = 200;

// Matches API's RawSurfaceFitter.CircleFixMode (RedAlign.API.Service) - bare int, same
// convention as common.js's DataFilterTypes. No production equivalent yet (Adaptive is
// diagnostic-only for now), so there's no PRODUCTION_ default the way CIRCLE_FIX_LIMIT has one.
export const CircleFixMode = { Fixed: 0, Adaptive: 1 };
// Marshall's own validated default (graphapp-optimtize-data-grok-v2.md: "Adaptive CF1 (top bar,
// with k, default 3.0)"). Valid range 2.0-4.0 per GraphApp's FilterOptimizer.AdaptiveKMin/Max.
export const DEFAULT_ADAPTIVE_K = 3.0;

// Bench trainer's own measurements, microns, from the end of the motor shaft: to the front foot,
// then front foot to rear foot (Kevin, 2026-08-29 - "those are the actual distances when the
// bench trainer is scanned"). Pre-filled rather than left blank since this is the only rig this
// tab is used against in practice.
export const DEFAULT_DISTANCE_TO_FF = 28000;
export const DEFAULT_DISTANCE_FTOR = 127000;

// API/appsettings.json's own Calibration/DataFilters section (2026-08-29) - the starting
// position for the new Surface Correction / NoiseReductionScatter / FocusRemoveWorst panels,
// same "open on production's own settings" convention as the circle-fix/gate defaults above.
export const PRODUCTION_CORRECT_Y = true;
export const PRODUCTION_CORRECT_Z = false;
export const PRODUCTION_NRS_ITERATIONS = 1;
export const PRODUCTION_NRS_MULTIPLIER = 2.0;
export const PRODUCTION_NRS_KEEP_BEST_PERCENT = 0.50;
export const PRODUCTION_FOCUS_ITERATIONS = 5;
export const PRODUCTION_FOCUS_ELIMINATE_PERCENT = 0.10;
export const PRODUCTION_FOCUS_KEEP_BEST_PERCENT = 0.90;

const initialState = {
    captureId: null,
    // When the sweep was taken, from the capture's own ScanStart block. Sent to scan/parse so
    // shims use the calibration in effect at capture time, not whatever is newest now - a
    // calibration records the actuator's bow when it was taken, and that changes with
    // temperature (~7um of Z shim error per degree of mismatch, measured 2026-09-08).
    capturedAt: null,
    // Which calibration the last shim result actually used: { calibrationId, dateTime,
    // scanCount, predatesCapture }. Shown with the shims because the same capture against a
    // different calibration gives different numbers.
    appliedCalibration: null,
    fileName: "",
    // Per-line fit results from the API: x, centerY, centerZ, radius, sigma, centralAngle,
    // pointCount, kept, dropReason. Around 2000 rows for a typical capture - small enough to
    // hold and plot whole, which is why there is no client-side decimation here.
    circles: [],
    lineCount: 0,
    lineWidth: 0,
    kept: 0,
    dropped: 0,
    // Each threshold's *value* (the slider position) and whether it's *enabled* (the switch)
    // are tracked separately, so flipping a switch off and back on restores exactly where the
    // slider was - matching the mockup's independent switch+slider pairs. Circle fix/angle/point
    // default on (production applies all three); sigma defaults off (production has no residual
    // gate).
    circleFixLimit: PRODUCTION_CIRCLE_FIX_LIMIT,
    circleFixEnabled: true,
    // Adaptive is the default fit mode (Kevin, 2026-08-29) - Fixed remains available as the
    // production-matching baseline for comparison, but Adaptive is the recommended starting point.
    circleFixMode: CircleFixMode.Adaptive,
    adaptiveK: DEFAULT_ADAPTIVE_K,
    angleLimit: PRODUCTION_ANGLE_LIMIT,
    angleEnabled: true,
    pointLimit: PRODUCTION_POINT_LIMIT,
    pointEnabled: true,
    maxSigma: DEFAULT_MAX_SIGMA,
    sigmaEnabled: false,
    selectedCircleX: null,
    status: "idle",     // idle | uploading | fitting | ready | error
    error: null,

    // Raw y/z points plus the original/fixed fit summaries for the selected circle's line,
    // fetched on demand for the drilldown panel's before/after view - not part of the /fit
    // payload, which is deliberately just the gated summary (see the remarks on
    // RawSurfaceLineResponse). Keyed implicitly by selectedCircleX; loadLinePoints guards against
    // a stale response landing after the user has already moved to another circle.
    linePoints: null,       // { x, y: [], z: [], micronLimit, removed: [], original: {...}, fixed: {...} }
    linePointsStatus: "idle", // idle | loading | ready | error
    linePointsError: null,

    // Pooled signed-residual-vs-polar-angle profile for the whole capture (AngleProfileResponse) -
    // the diagnostic behind the geofencing/nipple-clipping work. Not fetched with the fit: it is a
    // full re-walk of every line, so it loads only when explicitly asked for.
    // Angular clipping, both off by default so the view opens on "what production would do".
    // Independent of circleFixEnabled: clipping is a filter in its own right, not a fixing mode,
    // and it runs even with fixing switched off.
    nippleClipEnabled: false,
    edgeClipEnabled: false,
    clipZones: [],
    // Per-circle centre shift caused by clipping alone (clipped - unclipped, same fixing both
    // sides). Empty when clipping is off. Drives the comparison overlay on the Y/Z charts.
    clipDeltas: [],

    angleProfile: null,       // { binDegrees, circlesUsed, original: [...], fixed: [...] }
    angleProfileStatus: "idle", // idle | loading | ready | error
    angleProfileError: null,

    // Captures previously uploaded for the selected Alignment Unit, for the "Raw Capture"
    // picker - metadata only (id/filename/description/timestamp), not the fit itself.
    selectedAuId: "",
    captures: [],
    capturesStatus: "idle", // idle | loading | ready | error

    // Machine/Motor shaft ranges (shaft-position microns, same units as circle.x), set by
    // dragging across a chart and confirming which shaft it belongs to. Unlike Scan Analysis,
    // there is no automatic midpoint-based assignment here - the two are independent, so it is
    // a real choice with a real possibility of overlap. Groundwork for the Save/Calculate modal's
    // range prefill, which does not exist yet.
    machineRange: null, // { start, stop }
    motorRange: null,   // { start, stop }

    // Foot-spacing measurements, microns, from the end of the motor shaft: to the front foot,
    // then front foot to rear foot. Pre-filled with the bench trainer's own real measurements
    // (Kevin, 2026-08-29), unlike machineRange/motorRange which start blank - unlike shaft
    // ranges (which genuinely vary per capture), the foot positions are the same physical rig
    // every time this tab is used, so opening on a real, editable value beats opening on null.
    distanceToFF: DEFAULT_DISTANCE_TO_FF,
    distanceFtoR: DEFAULT_DISTANCE_FTOR,

    // Surface Correction / Data Filter panel state - gated behind rangesAndFeetSet (see
    // RawSurfaceSettings.js), so these only take effect once shaft ranges and foot positions are
    // set. Opens on production's own settings, same convention as circleFixLimit/angleLimit/etc.
    correctY: PRODUCTION_CORRECT_Y,
    correctZ: PRODUCTION_CORRECT_Z,
    nrsEnabled: true,
    nrsIterations: PRODUCTION_NRS_ITERATIONS,
    nrsMultiplier: PRODUCTION_NRS_MULTIPLIER,
    nrsKeepBestPercent: PRODUCTION_NRS_KEEP_BEST_PERCENT,
    focusEnabled: true,
    focusIterations: PRODUCTION_FOCUS_ITERATIONS,
    focusEliminatePercent: PRODUCTION_FOCUS_ELIMINATE_PERCENT,
    focusKeepBestPercent: PRODUCTION_FOCUS_KEEP_BEST_PERCENT,
    // Machine/motor trace stats from the last recompute (scan.Trace, now returned by scan/parse) -
    // what the new filter cards' trace tables read. Null until the first preview/save completes.
    filterTrace: null,
    // Machine/motor best-fit-line stats (pointCount/slope/intercept/standardDeviation/rSquared)
    // from the last recompute (scan.Result.summary) - what the Shim Values card's stats table
    // reads, same shape as Scan Analysis's own laserData.summary. Null until the first
    // preview/save completes.
    summary: null,
    // Separate from `status` (which covers fitting/circle-fixing) - a shim/filter-panel recompute
    // doesn't touch circles at all, so it gets its own busy indicator rather than disabling the
    // circle-fix controls too, which have nothing to do with this request.
    shimStatus: "idle", // idle | computing | ready | error

    // Off by default (Kevin, 2026-08-10) - rejected circles clutter the plot more than they
    // help by default; the Fit Summary card's Rejected count and the drilldown's per-circle
    // detail already cover "what got dropped and why" without drawing every one of them.
    showRejected: false,

    // Last "Calculate Shim Values" (preview) result from RawSurfaceSaveDialog, shown in the
    // Shaft Ranges card too so it stays visible after the dialog closes - explicitly a preview,
    // never persisted itself, so it's cleared (see applyFit/setSelectedAuId below) whenever the
    // underlying fit changes and would make it stale/misleading.
    shims: null,
    // Whether `shims` came from a save rather than a Calculate preview.
    shimsSaved: false,

    // Scans already saved from the loaded capture, newest first, each with the settings that
    // produced it and the shim values that resulted. Drives both the "Compare Saved Scans" button's
    // enabled state and the table behind it.
    savedScans: [],
    savedScansStatus: "idle", // idle | loading | ready | error
};

/**
 * Compresses and uploads a capture, then fits it.
 *
 * Compression happens in the browser via CompressionStream so the ~9-44MB of JSON never
 * crosses the wire uncompressed (measured ~11x reduction). CompressionStream exposes no
 * level setting, which is fine: measurement showed level 6 - roughly what it uses - is the
 * right trade anyway, since level 9 costs about 5x the time to save a further 13%.
 *
 * auid is required (the API rejects an upload without one) so every capture can be found again
 * later via the "Raw Capture" picker - otherwise an upload would be a dead end the moment the
 * tab is closed.
 */
export const uploadCapture = createAsyncThunk(
    "rawSurface/upload",
    async (args, { getState, rejectWithValue }) => {
        try {
            const state = getState().rawSurface;
            const token = getState().auth.token;

            // Accessed off window rather than as a bare global: CompressionStream is newer than
            // the lint config's browser globals list, and this also makes the capability check
            // explicit rather than relying on a typeof guard.
            const Compressor = window.CompressionStream;
            if (!Compressor) {
                return rejectWithValue(
                    "This browser does not support CompressionStream; a capture cannot be compressed for upload."
                );
            }

            const stream = new Blob([args.contents])
                .stream()
                .pipeThrough(new Compressor("gzip"));
            const gzipped = await new Response(stream).blob();

            const query = new URLSearchParams({
                auid: args.auid,
                fileName: args.fileName,
                description: args.description ?? "",
            });

            // Sent as a raw gzip body, not multipart or JSON: the API stores these bytes
            // verbatim without ever decompressing them on the upload path. auid/fileName/
            // description travel as query params instead, since the body is the gzip stream
            // itself with no envelope to carry them in.
            const { captureId } = await new api().requestBinary(
                `rawsurface?${query.toString()}`, token, "post", gzipped, "application/gzip"
            );

            const fit = await new api().request(
                `rawsurface/${captureId}/fit?angleLimit=${PRODUCTION_ANGLE_LIMIT}&pointLimit=${PRODUCTION_POINT_LIMIT}` +
                `&micronLimit=${PRODUCTION_CIRCLE_FIX_LIMIT}&mode=${state.circleFixMode}&adaptiveK=${state.adaptiveK}`,
                token, "get"
            );

            return { fileName: args.fileName, fit };
        } catch (err) {
            return rejectWithValue(err?.message ?? "Upload failed");
        }
    }
);

/** Loads a previously-uploaded capture by id - no upload, just the existing fit. */
export const loadCapture = createAsyncThunk(
    "rawSurface/loadCapture",
    async (args, { getState, rejectWithValue }) => {
        try {
            const state = getState().rawSurface;
            const token = getState().auth.token;
            const fit = await new api().request(
                `rawsurface/${args.captureId}/fit?angleLimit=${PRODUCTION_ANGLE_LIMIT}&pointLimit=${PRODUCTION_POINT_LIMIT}` +
                `&micronLimit=${PRODUCTION_CIRCLE_FIX_LIMIT}&mode=${state.circleFixMode}&adaptiveK=${state.adaptiveK}`,
                token, "get"
            );
            return { fileName: args.fileName, fit };
        } catch (err) {
            return rejectWithValue(err?.message ?? "Load failed");
        }
    }
);

/** Lists captures previously uploaded for an Alignment Unit, for the "Raw Capture" picker. */
export const loadCaptureList = createAsyncThunk(
    "rawSurface/loadCaptureList",
    async (args, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const captures = await new api().request(`rawsurface/au/${args.auid}`, token, "get");
            return captures;
        } catch (err) {
            return rejectWithValue(err?.message ?? "Failed to load captures");
        }
    }
);

/**
 * Lists scans previously saved from a capture, with the settings and shim values each produced.
 *
 * In Redux rather than local dialog state because two components need it: the rail footer decides
 * whether to enable "Compare Saved Scans", and the dialog renders the table.
 */
export const loadCaptureScans = createAsyncThunk(
    "rawSurface/loadCaptureScans",
    async (args, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const scans = await new api().request(`rawsurface/${args.captureId}/scans`, token, "get");
            return scans ?? [];
        } catch (err) {
            return rejectWithValue(err?.message ?? "Failed to load saved scans");
        }
    }
);

/**
 * Re-applies circle fixing and acceptance thresholds to an already-fitted capture.
 *
 * Angle/point/sigma gating alone is a pure filter on the server's cached fit, never a re-fit, so
 * it stays cheap enough to drive from slider input. Circle fixing is different - it prunes points
 * and genuinely re-fits - but the server (RawSurfaceController.BuildResponse) caches the fixed
 * result too and only recomputes it when MicronLimit actually changes, and always resolves it
 * before gating, so this action can send all four in one request without the client having to
 * worry about ordering.
 */
export const applyThresholds = createAsyncThunk(
    "rawSurface/applyThresholds",
    async (args, { getState, dispatch, rejectWithValue }) => {
        try {
            const state = getState().rawSurface;
            const token = getState().auth.token;

            const circleFixEnabled = args.circleFixEnabled ?? state.circleFixEnabled;
            const angleEnabled = args.angleEnabled ?? state.angleEnabled;
            const pointEnabled = args.pointEnabled ?? state.pointEnabled;
            const sigmaEnabled = args.sigmaEnabled ?? state.sigmaEnabled;
            const circleFixMode = args.circleFixMode ?? state.circleFixMode;
            const adaptiveK = args.adaptiveK ?? state.adaptiveK;

            const fit = await new api().request(
                `rawsurface/${state.captureId}/filter`, token, "post",
                {
                    // A disabled threshold sends its "off" value (0 / null) to the server
                    // regardless of the remembered slider position - see applyFit below for how
                    // that position survives the round trip instead of being echoed back as 0.
                    // A disabled switch must force mode back to Fixed too, not just zero
                    // micronLimit - the server treats mode === Adaptive as "fixing requested"
                    // regardless of micronLimit's value (Adaptive doesn't use that parameter),
                    // so leaving Adaptive selected here would keep fixing running server-side
                    // even with the switch off.
                    micronLimit: circleFixEnabled ? (args.circleFixLimit ?? state.circleFixLimit) : 0,
                    mode: circleFixEnabled ? circleFixMode : CircleFixMode.Fixed,
                    adaptiveK,
                    angleLimit: angleEnabled ? (args.angleLimit ?? state.angleLimit) : 0,
                    pointLimit: pointEnabled ? (args.pointLimit ?? state.pointLimit) : 0,
                    maxSigma: sigmaEnabled
                        ? (args.maxSigma !== undefined ? args.maxSigma : state.maxSigma)
                        : null,
                    nippleClipEnabled: args.nippleClipEnabled ?? state.nippleClipEnabled,
                    edgeClipEnabled: args.edgeClipEnabled ?? state.edgeClipEnabled,
                    // Sent so each shaft is clipped with its own measured window rather than a
                    // blend of both - the server keys its cached profiles on these spans.
                    machineStart: state.machineRange ? Math.round(state.machineRange.start) : null,
                    machineStop: state.machineRange ? Math.round(state.machineRange.stop) : null,
                    motorStart: state.motorRange ? Math.round(state.motorRange.start) : null,
                    motorStop: state.motorRange ? Math.round(state.motorRange.stop) : null,
                }
            );

            // The drilldown's before/after view always requests the CURRENT circleFixLimit (see
            // loadLinePoints), independent of circleFixEnabled - if a circle is open and that
            // limit just changed, the already-loaded line data is now stale (it was fetched with
            // the old limit) and would silently show a comparison that no longer matches the
            // slider. Compare against what the loaded data was actually fetched with, not against
            // circleFixLimit before/after: by the time this thunk runs, the slider's own
            // setCircleFixLimit has already committed the new value to state, so diffing
            // circleFixLimit itself would never detect a change.
            if (state.selectedCircleX !== null && state.linePoints?.micronLimit !== state.circleFixLimit) {
                dispatch(loadLinePoints({ x: state.selectedCircleX }));
            }

            return fit;
        } catch (err) {
            return rejectWithValue(err?.message ?? "Filter failed");
        }
    }
);

/**
 * Recomputes shim values from the currently-kept circles, with the Surface Correction / Data
 * Filter panels' live settings - the "preview" half of scan/parse (see RawSurfaceSaveDialog.js's
 * submit(), which this mirrors for its own settings), fired automatically as those settings
 * change rather than through the dialog. "Save as Scan" stays dialog-only; this never persists.
 *
 * Only meaningful once shaft ranges and foot positions are set (see rangesAndFeetSet in
 * RawSurfaceSettings.js) - the cards that dispatch this are gated behind that already, but this
 * guards directly against a stray call too, since a preview with no ranges would either fail
 * server-side or silently use the API's configured defaults instead of what's actually shown.
 */
export const applyRawSurfaceFilters = createAsyncThunk(
    "rawSurface/applyRawSurfaceFilters",
    async (_args, { getState, rejectWithValue }) => {
        try {
            const state = getState().rawSurface;
            const token = getState().auth.token;

            if (!state.machineRange || !state.motorRange
                || state.distanceToFF == null || state.distanceFtoR == null) {
                return rejectWithValue("Shaft ranges and foot positions must be set first.");
            }

            // Response is { shims, filterTrace } - SaveTSV now returns both (previously bare
            // ShimValues; see ScanController.cs), so this and RawSurfaceSaveDialog.js's submit()
            // both read result.shims/result.filterTrace, not the result itself as the shim.
            const result = await new api().request(
                "scan/parse", token, "post",
                {
                    data: circlesToTsv(state.circles),
                    description: "",
                    auid: state.selectedAuId,
                    preview: true,
                    captureId: state.captureId,
                    capturedat: state.capturedAt,

                    machinestart: state.machineRange.start,
                    machinestop: state.machineRange.stop,
                    motorstart: state.motorRange.start,
                    motorstop: state.motorRange.stop,
                    distancetoff: state.distanceToFF,
                    distanceftor: state.distanceFtoR,
                    motorright: computeMotorRight(state.machineRange, state.motorRange),

                    correctY: state.correctY,
                    correctZ: state.correctZ,
                    nrsEnabled: state.nrsEnabled,
                    nrsIterations: state.nrsIterations,
                    nrsMultiplier: state.nrsMultiplier,
                    nrsKeepBestPercent: state.nrsKeepBestPercent,
                    focusEnabled: state.focusEnabled,
                    focusIterations: state.focusIterations,
                    focusEliminatePercent: state.focusEliminatePercent,
                    focusKeepBestPercent: state.focusKeepBestPercent,
                }
            );

            return result;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.detail ?? err?.message ?? "Filter preview failed");
        }
    }
);

/**
 * Fetches one line's raw y/z points plus its original and circle-fixed fit, for the drilldown
 * panel's before/after comparison - a small, cheap per-line computation against the capture the
 * server already has cached from the fit (see RawSurfaceController.Line), not a re-fit of the
 * whole capture and not a fresh blob load on a cache hit.
 *
 * Always requests the CURRENT circleFixLimit slider value, regardless of whether circleFixEnabled
 * is presently toggled on - the before/after comparison is meant to answer "what would circle
 * fixing at this limit do", independent of whether the main view has fixing switched on right now.
 */
export const loadLinePoints = createAsyncThunk(
    "rawSurface/loadLinePoints",
    async (args, { getState, rejectWithValue }) => {
        try {
            const state = getState().rawSurface;
            const token = getState().auth.token;
            const line = await new api().request(
                `rawsurface/${state.captureId}/line?x=${args.x}&micronLimit=${state.circleFixLimit}` +
                `&mode=${state.circleFixMode}&adaptiveK=${state.adaptiveK}`,
                token, "get"
            );
            return line;
        } catch (err) {
            return rejectWithValue(err?.message ?? "Failed to load raw points");
        }
    }
);

/**
 * Loads the pooled residual-vs-angle profile for the whole capture.
 *
 * Deliberately NOT dispatched by applyRawSurfaceFilters the way loadLinePoints is: this walks and
 * re-fixes every line in the capture rather than reading a cached reduction, so it is far too
 * expensive to ride along on a slider change. It is a one-shot analysis the user asks for.
 *
 * Sends the current circle-fix settings so the "after fixing" series reflects whatever is on the
 * Circle Fit card right now - the whole question this answers is whether the fixing presently
 * configured actually removes the nipple.
 */
export const loadAngleProfile = createAsyncThunk(
    "rawSurface/loadAngleProfile",
    async (args, { getState, rejectWithValue }) => {
        try {
            const state = getState().rawSurface;
            const token = getState().auth.token;

            // Restrict to a shaft range when one is set: the profile should describe the shaft,
            // not the empty air, fixtures and shaft ends a full sweep also crosses. Machine and
            // motor are independent ranges here (no midpoint split), so this takes whichever the
            // caller asked for rather than assuming one.
            const range = args?.range ?? null;
            const rangeQuery = range ? `&xStart=${Math.round(range.start)}&xStop=${Math.round(range.stop)}` : "";

            const profile = await new api().request(
                `rawsurface/${state.captureId}/angleprofile?micronLimit=${state.circleFixLimit}` +
                `&mode=${state.circleFixMode}&adaptiveK=${state.adaptiveK}` +
                `&binDegrees=${args?.binDegrees ?? 0.5}${rangeQuery}`,
                token, "get"
            );

            // Stamp the settings this ran with. The card needs them to say "this curve is from
            // the previous circle-fix settings" rather than silently showing a stale comparison,
            // and they are not in the response - the server echoes nothing back.
            return {
                ...profile,
                requestedWith: {
                    circleFixLimit: state.circleFixLimit,
                    circleFixMode: state.circleFixMode,
                    adaptiveK: state.adaptiveK,
                    range,
                },
            };
        } catch (err) {
            return rejectWithValue(err?.message ?? "Failed to load angle profile");
        }
    }
);

/**
 * Selects a circle and, unless closing (x === null), kicks off its raw-points fetch in the same
 * step - the two always travel together from every call site (chart click, drilldown prev/next),
 * so pairing them here means neither caller has to remember to dispatch both.
 */
export const openCircle = (x) => (dispatch) => {
    dispatch(selectCircle({ x }));
    if (x !== null) {
        dispatch(loadLinePoints({ x }));
    }
};

const applyFit = (state, fit) => ({
    ...state,
    captureId: fit.captureId,
    // Sticky: only the /fit response carries it, and the /filter response reuses applyFit.
    capturedAt: fit.capturedAt ?? state.capturedAt,
    circles: fit.circles ?? [],
    lineCount: fit.lineCount,
    lineWidth: fit.lineWidth,
    kept: fit.kept,
    dropped: fit.dropped,
    // The server echoes back whatever was sent, which is 0/null for a disabled threshold -
    // keep the remembered slider position in that case instead of letting the echo zero it out,
    // so switching a threshold back on restores where it was rather than resetting to 0.
    circleFixLimit: state.circleFixEnabled ? fit.micronLimit : state.circleFixLimit,
    // Not conditional on circleFixEnabled the way circleFixLimit is - mode/adaptiveK are never
    // sent as an "off" sentinel (see applyThresholds), so the server's echo is always the real
    // current value regardless of whether fixing is presently enabled.
    circleFixMode: fit.circleFixMode ?? state.circleFixMode,
    clipZones: fit.clipZones ?? [],
    clipDeltas: fit.clipDeltas ?? [],
    adaptiveK: fit.adaptiveK ?? state.adaptiveK,
    angleLimit: state.angleEnabled ? fit.angleLimit : state.angleLimit,
    pointLimit: state.pointEnabled ? fit.pointLimit : state.pointLimit,
    maxSigma: state.sigmaEnabled ? fit.maxSigma : state.maxSigma,
    status: "ready",
    error: null,
    // The kept-circle set just changed (new capture, or fix/gating thresholds moved) - any
    // previously-calculated shim preview no longer reflects what's currently kept.
    shims: null,
    shimsSaved: false,
    filterTrace: null,
    summary: null,
    // Only a different capture invalidates the saved-scan list. applyFit also runs on every
    // threshold change, where the list is still correct and clearing it would blank the Compare
    // button mid-interaction.
    savedScans: fit.captureId === state.captureId ? state.savedScans : [],
    savedScansStatus: fit.captureId === state.captureId ? state.savedScansStatus : "idle",
    // Same rule as savedScans: the profile describes a whole capture, so a threshold change
    // leaves it valid, but a different capture makes it wrong. Note a circle-fix change does NOT
    // clear it - the "after fixing" series would be stale, which the card says out loud rather
    // than throwing away a curve that takes a full re-walk of the capture to rebuild.
    angleProfile: fit.captureId === state.captureId ? state.angleProfile : null,
    angleProfileStatus: fit.captureId === state.captureId ? state.angleProfileStatus : "idle",
});

export const RawSurfaceSlice = createSlice({
    name: "rawSurface",
    initialState,
    reducers: {
        // Local threshold edits so sliders stay responsive; the request is dispatched
        // separately once the value settles.
        setCircleFixLimit: (state, action) => ({ ...state, circleFixLimit: action.payload.circleFixLimit }),
        setCircleFixMode: (state, action) => ({ ...state, circleFixMode: action.payload.circleFixMode }),
        setAdaptiveK: (state, action) => ({ ...state, adaptiveK: action.payload.adaptiveK }),
        setAngleLimit: (state, action) => ({ ...state, angleLimit: action.payload.angleLimit }),
        setPointLimit: (state, action) => ({ ...state, pointLimit: action.payload.pointLimit }),
        setMaxSigma: (state, action) => ({ ...state, maxSigma: action.payload.maxSigma }),
        // The switch next to each slider - independent of the slider's own value (see the note
        // on angleLimit/angleEnabled in initialState).
        setCircleFixEnabled: (state, action) => ({ ...state, circleFixEnabled: action.payload.enabled }),
        setAngleEnabled: (state, action) => ({ ...state, angleEnabled: action.payload.enabled }),
        setPointEnabled: (state, action) => ({ ...state, pointEnabled: action.payload.enabled }),
        setSigmaEnabled: (state, action) => ({ ...state, sigmaEnabled: action.payload.enabled }),
        selectCircle: (state, action) => ({
            ...state,
            selectedCircleX: action.payload.x,
            // Closing (x === null) drops any in-flight/loaded points rather than leaving them to
            // flash stale on the next circle before its own fetch resolves.
            ...(action.payload.x === null
                ? { linePoints: null, linePointsStatus: "idle", linePointsError: null }
                : {}),
        }),
        // shaft is "machine" or "motor" - a drag-select confirmed as belonging to that shaft.
        setShaftRange: (state, action) => ({
            ...state,
            [`${action.payload.shaft}Range`]: { start: action.payload.start, stop: action.payload.stop },
        }),
        clearShaftRange: (state, action) => ({
            ...state,
            [`${action.payload.shaft}Range`]: null,
        }),
        setShowRejected: (state, action) => ({ ...state, showRejected: action.payload.show }),
        setNippleClipEnabled: (state, action) => ({ ...state, nippleClipEnabled: action.payload.enabled }),
        setEdgeClipEnabled: (state, action) => ({ ...state, edgeClipEnabled: action.payload.enabled }),
        setDistanceToFF: (state, action) => ({ ...state, distanceToFF: action.payload.distanceToFF }),
        setDistanceFtoR: (state, action) => ({ ...state, distanceFtoR: action.payload.distanceFtoR }),
        setCorrectY: (state, action) => ({ ...state, correctY: action.payload.correctY }),
        setCorrectZ: (state, action) => ({ ...state, correctZ: action.payload.correctZ }),
        setNrsEnabled: (state, action) => ({ ...state, nrsEnabled: action.payload.enabled }),
        setNrsIterations: (state, action) => ({ ...state, nrsIterations: action.payload.nrsIterations }),
        setNrsMultiplier: (state, action) => ({ ...state, nrsMultiplier: action.payload.nrsMultiplier }),
        setNrsKeepBestPercent: (state, action) => (
            { ...state, nrsKeepBestPercent: action.payload.nrsKeepBestPercent }
        ),
        setFocusEnabled: (state, action) => ({ ...state, focusEnabled: action.payload.enabled }),
        setFocusIterations: (state, action) => ({ ...state, focusIterations: action.payload.focusIterations }),
        setFocusEliminatePercent: (state, action) => (
            { ...state, focusEliminatePercent: action.payload.focusEliminatePercent }
        ),
        setFocusKeepBestPercent: (state, action) => (
            { ...state, focusKeepBestPercent: action.payload.focusKeepBestPercent }
        ),
        // action.payload.shims is the ShimValues from a "Calculate Shim Values" preview, or null
        // to clear it (dialog re-opened for a fresh calculation, or the preview is being shown
        // is no longer valid - see applyFit/setSelectedAuId for the other clearing points).
        // `saved` distinguishes a Calculate preview from the result of an actual save. Without it
        // the card kept saying "not saved" after a save, because Save neither cleared nor replaced
        // the preview the user had run first.
        setShims: (state, action) => ({
            ...state,
            shims: action.payload.shims,
            shimsSaved: action.payload.saved ?? false,
            // Left alone when absent, so clearing the shims doesn't also drop the record of
            // which calibration the last real result used.
            appliedCalibration: action.payload.calibration ?? state.appliedCalibration,
            // Optional - RawSurfaceSaveDialog.js's Calculate/Save now also get filterTrace/summary
            // back from scan/parse (see applyRawSurfaceFilters), same shape. Left unchanged when
            // not provided (e.g. the `shims: null` clear-before-recompute call).
            ...(action.payload.filterTrace !== undefined ? { filterTrace: action.payload.filterTrace } : {}),
            ...(action.payload.summary !== undefined ? { summary: action.payload.summary } : {}),
        }),
        // Unloads whatever capture was loaded for the previous AU - mirrors Scan Analysis's
        // resetData() on AU switch (DataWatcher.js's handleSelectAU). Threshold settings
        // (angleLimit/pointEnabled/maxSigma/showRejected, etc.) are left alone, same as Scan
        // Analysis leaves its filter settings (CalcSettingsSlice) untouched on AU switch.
        setSelectedAuId: (state, action) => ({
            ...state,
            selectedAuId: action.payload.auid,
            captures: [],
            capturesStatus: "idle",
            captureId: null,
            capturedAt: null,
            appliedCalibration: null,
            fileName: "",
            circles: [],
            lineCount: 0,
            lineWidth: 0,
            kept: 0,
            dropped: 0,
            status: "idle",
            error: null,
            selectedCircleX: null,
            linePoints: null,
            linePointsStatus: "idle",
            linePointsError: null,
            angleProfile: null,
            angleProfileStatus: "idle",
            angleProfileError: null,
            machineRange: null,
            motorRange: null,
            distanceToFF: DEFAULT_DISTANCE_TO_FF,
            distanceFtoR: DEFAULT_DISTANCE_FTOR,
            shims: null,
            shimsSaved: false,
            filterTrace: null,
            summary: null,
            savedScans: [],
            savedScansStatus: "idle",
        }),
        resetRawSurface: (state) => ({
            ...initialState, selectedAuId: state.selectedAuId, captures: state.captures,
        }),
    },
    extraReducers(builder) {
        builder
            .addCase(uploadCapture.pending, (state) => ({
                ...state, status: "uploading", error: null,
            }))
            .addCase(uploadCapture.fulfilled, (state, action) => ({
                ...applyFit(state, action.payload.fit),
                fileName: action.payload.fileName,
            }))
            .addCase(uploadCapture.rejected, (state, action) => ({
                ...state, status: "error", error: action.payload ?? "Upload failed",
            }))
            .addCase(loadCapture.pending, (state) => ({
                ...state, status: "fitting", error: null,
            }))
            .addCase(loadCapture.fulfilled, (state, action) => ({
                ...applyFit(state, action.payload.fit),
                fileName: action.payload.fileName,
            }))
            .addCase(loadCapture.rejected, (state, action) => ({
                ...state, status: "error", error: action.payload ?? "Load failed",
            }))
            .addCase(loadCaptureList.pending, (state) => ({
                ...state, capturesStatus: "loading",
            }))
            .addCase(loadCaptureList.fulfilled, (state, action) => ({
                ...state, captures: action.payload, capturesStatus: "ready",
            }))
            .addCase(loadCaptureList.rejected, (state, action) => ({
                ...state, capturesStatus: "error", error: action.payload ?? "Failed to load captures",
            }))
            .addCase(loadCaptureScans.pending, (state) => ({
                ...state, savedScansStatus: "loading",
            }))
            .addCase(loadCaptureScans.fulfilled, (state, action) => ({
                ...state, savedScans: action.payload, savedScansStatus: "ready",
            }))
            .addCase(loadCaptureScans.rejected, (state) => ({
                // Deliberately not surfaced through `error`, which drives the tab's own failure
                // banner: not being able to list previous saves should not read as the capture
                // itself having failed to load.
                ...state, savedScans: [], savedScansStatus: "error",
            }))
            .addCase(applyThresholds.pending, (state) => ({ ...state, status: "fitting" }))
            .addCase(applyThresholds.fulfilled, (state, action) => applyFit(state, action.payload))
            .addCase(applyThresholds.rejected, (state, action) => ({
                ...state, status: "error", error: action.payload ?? "Filter failed",
            }))
            .addCase(applyRawSurfaceFilters.pending, (state) => ({ ...state, shimStatus: "computing" }))
            .addCase(applyRawSurfaceFilters.fulfilled, (state, action) => ({
                ...state,
                shims: action.payload.shims,
                shimsSaved: false,
                filterTrace: action.payload.filterTrace,
                summary: action.payload.summary,
                appliedCalibration: action.payload.calibration ?? null,
                shimStatus: "ready",
            }))
            .addCase(applyRawSurfaceFilters.rejected, (state, action) => ({
                ...state, shimStatus: "error", error: action.payload ?? "Filter preview failed",
            }))
            .addCase(loadLinePoints.pending, (state, action) => (
                // Guards against a slower, now-superseded request's pending/fulfilled/rejected
                // arriving after a newer one already changed which circle is selected.
                action.meta.arg.x !== state.selectedCircleX ? state : {
                    ...state, linePoints: null, linePointsStatus: "loading", linePointsError: null,
                }
            ))
            .addCase(loadLinePoints.fulfilled, (state, action) => (
                action.meta.arg.x !== state.selectedCircleX ? state : {
                    ...state, linePoints: action.payload, linePointsStatus: "ready",
                }
            ))
            .addCase(loadLinePoints.rejected, (state, action) => (
                action.meta.arg.x !== state.selectedCircleX ? state : {
                    ...state,
                    linePointsStatus: "error",
                    linePointsError: action.payload ?? "Failed to load raw points",
                }
            ))
            .addCase(loadAngleProfile.pending, (state) => ({
                // The previous profile is kept on screen while a new one loads rather than
                // blanked: re-running it after changing circle-fix settings is the main way this
                // view gets used, and losing the old curve mid-request makes the two harder to
                // compare, not easier.
                ...state, angleProfileStatus: "loading", angleProfileError: null,
            }))
            .addCase(loadAngleProfile.fulfilled, (state, action) => ({
                ...state, angleProfile: action.payload, angleProfileStatus: "ready",
            }))
            .addCase(loadAngleProfile.rejected, (state, action) => ({
                ...state,
                angleProfileStatus: "error",
                angleProfileError: action.payload ?? "Failed to load angle profile",
            }));
    },
});

export const {
    setCircleFixLimit, setCircleFixEnabled, setCircleFixMode, setAdaptiveK,
    setAngleLimit, setPointLimit, setMaxSigma, setAngleEnabled, setPointEnabled, setSigmaEnabled,
    selectCircle, setSelectedAuId, resetRawSurface, setShaftRange, clearShaftRange, setShowRejected,
    setNippleClipEnabled, setEdgeClipEnabled,
    setShims, setDistanceToFF, setDistanceFtoR,
    setCorrectY, setCorrectZ,
    setNrsEnabled, setNrsIterations, setNrsMultiplier, setNrsKeepBestPercent,
    setFocusEnabled, setFocusIterations, setFocusEliminatePercent, setFocusKeepBestPercent,
} = RawSurfaceSlice.actions;

export default RawSurfaceSlice.reducer;
